import { Box, Typography, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { StandardAlertInfo } from "../../../../../../../shared/components/alert/StandardAlertInfo"
import { UserWallet } from "../refillTypes"
import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import { StandardChip } from "../../../../../../../shared/components/badges/StandardChip"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import type { MonetaryAmount } from "../../../../../../currency/currencyTypes"

const useStyles = makeStyles()((theme) => ({
    title: {
        marginTop: "-3rem",
        paddingBottom: "1rem",
        textAlign: "center",
    },
    noWallets: {
        marginBottom: theme.spacing(2),
        textAlign: "center",
    },
    container: {
        padding: "4rem 1rem 6rem 1rem",
    },
    cardContainers: {
        margin: "0 auto",
        maxWidth: "44rem",
    },
    cardContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    card: {
        cursor: "pointer",
        width: "20rem",
        height: "12rem",
        borderRadius: ".5rem",
        padding: "1rem",
        background: "radial-gradient(circle, rgba(2,0,36,0) 0%, rgba(0,193,245,1) 70%)",
    },
}))

type SelectWalletDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    onSelect: (wallet: UserWallet) => void
    userWallets?: Array<UserWallet> | null
    errorMessage?: string | null
}>

export function SelectWalletDialog({ open, onClose, onSelect, userWallets, errorMessage }: SelectWalletDialogProps) {
    const { classes } = useStyles()

    return (
        <StandardDialog maxWidthProp="md" open={open} onClose={onClose}>
            <LocalizedStrict id="user-wallets-refill-select-card-dialog-title">
                <Typography variant="h4" className={classes.title}>
                    Select wallet
                </Typography>
            </LocalizedStrict>
            {errorMessage ? (
                <Box className={classes.cardContainers}>
                    <Stack className={classes.container} alignItems="center">
                        <Typography variant="body1" align="center">
                            {errorMessage}
                        </Typography>
                    </Stack>
                </Box>
            ) : userWallets?.length ? (
                <Box className={classes.cardContainers}>
                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        justifyContent={{ xs: "center", sm: "center", md: "flex-start" }}
                        spacing="2rem"
                        className={classes.container}
                    >
                        {userWallets?.map((wallet) => (
                            <UserCardView
                                key={wallet.acquirer.acquirerSetupId}
                                onSelect={() => onSelect(wallet)}
                                balance={wallet.balance}
                                statusLabel={wallet.statusLabel}
                                displayName={wallet?.displayName}
                                brandingLabel={wallet?.brandingLabel}
                            />
                        ))}
                    </Stack>
                </Box>
            ) : (
                <LocalizedStrict id="user-wallets-refill-select-card-dialog-no-wallets">
                    <Typography variant="body1" className={classes.noWallets}>
                        The merchant don't offer digital wallets for your company.
                    </Typography>
                </LocalizedStrict>
            )}
        </StandardDialog>
    )
}

type UserCardViewProps = Readonly<{
    balance: MonetaryAmount
    statusLabel?: string
    displayName?: string
    brandingLabel?: string
    onSelect: () => void
}>

export function UserCardView({ onSelect, balance, statusLabel, displayName, brandingLabel }: UserCardViewProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    return (
        <Stack direction="row" spacing="2rem" className={classes.cardContainer} onClick={onSelect}>
            <Box className={classes.card}>
                <Stack direction="column" height="100%" justifyContent="space-between">
                    <Stack direction="row" justifyContent="space-between">
                        <Box>
                            <LocalizedStrict id="user-wallets-refill-select-card-balance">
                                <Typography variant="body2" color="white">
                                    Balance:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="h5" color="white">
                                {moneyFactory.format(toDinero(balance))}
                            </Typography>
                        </Box>
                        {statusLabel && <StandardChip variant="black">{statusLabel}</StandardChip>}
                    </Stack>
                    <Stack direction="column">
                        <Typography color="white" variant="subtitle1">
                            {displayName}
                        </Typography>
                        <Typography color="white" variant="subtitle2">
                            {brandingLabel}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Stack>
    )
}
